import React, { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { UserPlus, BadgeCheck, Eye, TrendingUp, Sparkles, MessageSquare, CalendarClock, Home } from 'lucide-react';
import { StatsCard } from '../ui/StatsCard';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { CollapsibleSection } from './CollapsibleSection';
import type { ActivePlan } from './useActivePlan';

interface Metric { current: number; previous: number; }
interface TeamAgent { id: string; name: string; }
interface CycleStats {
  has_agency: boolean;
  scope?: string;
  window_start?: string;
  window_end?: string;
  published?: Metric;
  shared_props?: Metric;
  shared_reqs?: Metric;
  shared_props_7d?: number;
  shared_reqs_7d?: number;
  new_leads?: Metric;
  seguimientos?: Metric;
  citas?: Metric;
  captadas?: Metric;
  sales?: Metric;
  visits?: { basis: string; current: number; previous: number };
}

type Period = 'cycle' | 'week' | 'month' | 'year';
const PERIODS: Array<{ key: Period; label: string; prevWord: string }> = [
  { key: 'cycle', label: 'Ciclo', prevWord: 'Ciclo anterior' },
  { key: 'week', label: 'Semana', prevWord: 'Semana anterior' },
  { key: 'month', label: 'Mes', prevWord: 'Mes anterior' },
  { key: 'year', label: 'Año', prevWord: 'Año anterior' },
];

const COLORS: Array<'indigo' | 'emerald' | 'amber' | 'rose'> = ['indigo', 'emerald', 'rose', 'amber', 'emerald', 'indigo'];

function pctTrend(cur: number, prev: number): number | undefined {
  if (prev <= 0) return undefined;
  return Math.round(((cur - prev) / prev) * 100);
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('es-GT', { day: 'numeric', month: 'short' });
}

// Resolve the [start, end] window for a comparison period. get_cycle_stats
// compares this window against the equal-length window immediately before it.
function windowFor(period: Period, plan: ActivePlan | null): { start: Date; end: Date; caption: string } {
  const now = new Date();
  if (period === 'cycle') {
    // cycle_start/cycle_end are UTC-midnight calendar dates; parsing them as raw
    // instants renders the caption a day early in es-GT ("30 jun – 31 jul" for a
    // Jul 1 → Aug 1 cycle). Anchor to local midnight of that calendar day.
    const parseCal = (s: string): Date => {
      const [y, m, d] = s.slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, d);
    };
    const cs = plan?.membership?.cycle_start ? parseCal(plan.membership.cycle_start) : null;
    const ce = plan?.membership?.cycle_end ? parseCal(plan.membership.cycle_end) : null;
    // Only use the billing cycle when the dates are present AND valid (end > start).
    // Suspended / manually-adjusted accounts can carry inverted or missing cycle
    // dates, which would produce an invalid window — fall back to a rolling 30 days.
    if (cs && ce && ce.getTime() > cs.getTime()) {
      return { start: cs, end: ce, caption: `${fmtDate(cs)} – ${fmtDate(ce)} · vs ciclo anterior` };
    }
    return { start: new Date(now.getTime() - 30 * 86400000), end: now, caption: 'Últimos 30 días · vs período anterior' };
  }
  const days = period === 'week' ? 7 : period === 'month' ? 30 : 365;
  const captions: Record<Exclude<Period, 'cycle'>, string> = {
    week: 'Últimos 7 días · vs semana anterior',
    month: 'Últimos 30 días · vs mes anterior',
    year: 'Últimos 12 meses · vs año anterior',
  };
  return { start: new Date(now.getTime() - days * 86400000), end: now, caption: captions[period] };
}

export const CicloInsights: React.FC<{ plan: ActivePlan | null; theme?: 'light' | 'dark'; staggerIndex?: number }> = ({ plan, theme = 'dark', staggerIndex = 0 }) => {
  const isLight = theme !== 'dark';
  const { profile } = useAuth();
  const [period, setPeriod] = useState<Period>('cycle');
  const [stats, setStats] = useState<CycleStats | null>(null);
  const [agents, setAgents] = useState<TeamAgent[]>([]);
  const [agentFilter, setAgentFilter] = useState<string>('');

  // Team list for the per-agent filter — only privileged callers get
  // scope==='agency' back, so plain agents never fetch or see the picker.
  useEffect(() => {
    if (!supabase || stats?.scope !== 'agency' || !profile?.agency_id || agents.length > 0) return;
    let cancelled = false;
    supabase
      .from('profiles').select('id, full_name')
      .eq('agency_id', profile.agency_id).eq('status', 'active').order('full_name')
      .then(({ data }) => {
        if (!cancelled && data) setAgents(data.map((p: any) => ({ id: p.id, name: p.full_name || 'Sin nombre' })));
      });
    return () => { cancelled = true; };
  }, [stats?.scope, profile?.agency_id]);

  // Memoize the window so windowFor's `new Date()` doesn't churn every render
  // and drive the fetch effect into an infinite loop.
  const { startISO, endISO, caption } = useMemo(() => {
    const w = windowFor(period, plan);
    return { startISO: w.start.toISOString(), endISO: w.end.toISOString(), caption: w.caption };
  }, [period, plan?.membership?.cycle_start, plan?.membership?.cycle_end]);

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    supabase
      .rpc('get_cycle_stats', { p_start: startISO, p_end: endISO, p_agent: agentFilter || null })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setStats(data as CycleStats);
      });
    return () => { cancelled = true; };
  }, [startISO, endISO, agentFilter]);

  if (!stats || !stats.has_agency) return null;

  const prevWord = PERIODS.find(p => p.key === period)!.prevWord;

  // Visits compare month-to-date against a FULL previous month — prorate the
  // previous month to the same elapsed days so mid-month never reads as a
  // fake collapse.
  const now = new Date();
  const daysInPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  const visitsPrev = Math.round((stats.visits?.previous ?? 0) * Math.min(1, now.getDate() / daysInPrevMonth));

  // Inventory state (publicadas/compartidas) lives in "Estado actual" now;
  // this section owns flow metrics only. Ventas hides until the first
  // recorded sale — a permanent 0 is guilt, not information.
  const tiles = [
    { label: 'Nuevos prospectos', icon: UserPlus, cur: stats.new_leads?.current ?? 0, prev: stats.new_leads?.previous ?? 0, prevLabel: prevWord },
    { label: 'Seguimientos', icon: MessageSquare, cur: stats.seguimientos?.current ?? 0, prev: stats.seguimientos?.previous ?? 0, prevLabel: prevWord },
    { label: 'Citas agendadas', icon: CalendarClock, cur: stats.citas?.current ?? 0, prev: stats.citas?.previous ?? 0, prevLabel: prevWord },
    { label: 'Propiedades captadas', icon: Home, cur: stats.captadas?.current ?? 0, prev: stats.captadas?.previous ?? 0, prevLabel: prevWord },
    ...((stats.sales?.current ?? 0) > 0 || (stats.sales?.previous ?? 0) > 0
      ? [{ label: 'Ventas cerradas', icon: BadgeCheck, cur: stats.sales?.current ?? 0, prev: stats.sales?.previous ?? 0, prevLabel: prevWord }]
      : []),
    { label: 'Visitas web (mes)', icon: Eye, cur: stats.visits?.current ?? 0, prev: visitsPrev, prevLabel: 'A este punto del mes anterior' },
  ];

  // Reward moment: the best-improving metric this period (only positive deltas).
  const best = tiles
    .map(t => ({ ...t, trend: pctTrend(t.cur, t.prev) }))
    .filter(t => t.trend !== undefined && t.trend > 0)
    .sort((a, b) => (b.trend ?? 0) - (a.trend ?? 0))[0];

  return (
    <CollapsibleSection
      title="Tu ciclo en números"
      icon={TrendingUp}
      storageKey="dash_ciclo"
      theme={theme}
      staggerIndex={staggerIndex}
    >
      {/* Period selector + agent filter + window caption */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="text-xs text-slate-400">{caption}</span>
        {stats.scope === 'agency' && agents.length > 0 && (
          <select
            value={agentFilter}
            onChange={e => setAgentFilter(e.target.value)}
            className={clsx(
              'text-xs font-bold rounded-lg px-2.5 py-1.5 border outline-none cursor-pointer',
              isLight ? 'bg-white border-slate-200 text-slate-700' : 'bg-white/5 border-white/10 text-slate-200'
            )}
          >
            <option value="">Todo el equipo</option>
            {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <div className={clsx('inline-flex p-0.5 rounded-lg', isLight ? 'bg-slate-100' : 'bg-white/5')}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={clsx(
                'px-3 py-1 text-xs font-bold rounded-md transition-colors',
                period === p.key
                  ? (isLight ? 'bg-white text-indigo-600 shadow-sm' : 'bg-white/10 text-white')
                  : (isLight ? 'text-slate-500 hover:text-slate-700' : 'text-slate-400 hover:text-slate-200')
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {best && (
        <div className={clsx(
          'flex items-center gap-2 mb-4 px-4 py-2.5 rounded-xl text-xs font-bold',
          isLight ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500/10 text-emerald-300'
        )}>
          <Sparkles size={14} className="shrink-0" />
          Tu mejor métrica: {best.label} +{best.trend}% vs {best.prevLabel.toLowerCase()}.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
        {tiles.map((t, idx) => {
          const trend = pctTrend(t.cur, t.prev);
          const sublabel = t.prev === 0 && t.cur > 0
            ? `Nuevo (${t.prevLabel.toLowerCase()}: 0)`
            : `${t.prevLabel}: ${t.prev.toLocaleString('es-GT')}`;
          return (
            <div key={t.label} className="rounded-2xl">
              <StatsCard
                label={t.label}
                value={t.cur.toLocaleString('es-GT')}
                icon={t.icon}
                // Celebrate improvements; declines stay visible via the
                // sublabel without a big red badge shouting at the user.
                trend={trend !== undefined && trend > 0 ? trend : undefined}
                sublabel={sublabel}
                color={COLORS[idx]}
                theme={theme}
              />
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
};
